/**
 * Canonical keyboard shortcut registry for the business webapp.
 * Stores per-user overrides in localStorage; system shortcuts are not remappable.
 */

export const KEYBOARD_SHORTCUTS_CHANGED_EVENT = "starmesh-keyboard-shortcuts-changed";

/** @typedef {{ key: string, meta?: boolean, ctrl?: boolean, alt?: boolean, shift?: boolean }} KeyBinding */

/**
 * @typedef {object} ShortcutDefinition
 * @property {string} id
 * @property {string} label
 * @property {string} description
 * @property {"action-items" | "settings"} scope
 * @property {boolean} editable
 * @property {KeyBinding} defaultBinding
 */

/** @type {ShortcutDefinition[]} */
export const KEYBOARD_SHORTCUT_DEFINITIONS = [
  {
    id: "settings.close",
    label: "Close settings",
    description: "Leave settings and return to action items",
    scope: "settings",
    editable: false,
    defaultBinding: { key: "Escape" },
  },
  {
    id: "search.focus",
    label: "Search tasks",
    description: "Open or close the search field",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "/" },
  },
  {
    id: "task.add",
    label: "Add task",
    description: "Focus the new task field",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "n" },
  },
  {
    id: "task.voice",
    label: "Speak to add task",
    description: "Start or stop voice capture for a new task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "v" },
  },
  {
    id: "settings.open",
    label: "Open settings",
    description: "Go to app settings",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "," },
  },
  {
    id: "settings.connectors.open",
    label: "Open connectors",
    description: "Go to connector settings",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "." },
  },
  {
    id: "sidebar.close",
    label: "Close task details",
    description: "Close the details sidebar when it is open",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "Escape" },
  },
  {
    id: "task.next",
    label: "Next task",
    description: "Move keyboard focus to the next task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "j" },
  },
  {
    id: "task.previous",
    label: "Previous task",
    description: "Move keyboard focus to the previous task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "k" },
  },
  {
    id: "task.open",
    label: "Open task details",
    description: "Open details for the focused task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "Enter" },
  },
  {
    id: "task.complete",
    label: "Mark task done",
    description: "Toggle done for the focused task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "e" },
  },
  {
    id: "task.archive",
    label: "Archive task",
    description: "Archive the focused task",
    scope: "action-items",
    editable: true,
    defaultBinding: { key: "h" },
  },
];

/**
 * Storage key for a user's shortcut overrides.
 *
 * @example
 * keyboardShortcutsStorageKey("user@acme.com");
 * // "starmesh_keyboard_shortcuts_user@acme.com"
 */
export const keyboardShortcutsStorageKey = (userKey = "") => {
  const normalized = String(userKey || "anonymous").trim() || "anonymous";
  return `starmesh_keyboard_shortcuts_${normalized}`;
};

/**
 * Normalize a binding for storage / comparison.
 *
 * @example
 * normalizeBinding({ key: "N", meta: false });
 * // { key: "n" }
 */
export const normalizeBinding = (binding) => {
  if (!binding || typeof binding !== "object") return null;
  const key = String(binding.key || "").trim();
  if (!key) return null;
  const normalizedKey = key.length === 1 ? key.toLowerCase() : key;
  /** @type {KeyBinding} */
  const next = { key: normalizedKey };
  if (binding.meta) next.meta = true;
  if (binding.ctrl) next.ctrl = true;
  if (binding.alt) next.alt = true;
  if (binding.shift) next.shift = true;
  return next;
};

/**
 * Stable string id for a binding (collision checks).
 *
 * @example
 * bindingFingerprint({ key: "j" });
 * // "j"
 *
 * @example
 * bindingFingerprint({ key: ",", meta: true });
 * // "meta+,"
 */
export const bindingFingerprint = (binding) => {
  const normalized = normalizeBinding(binding);
  if (!normalized) return "";
  const parts = [];
  if (normalized.meta) parts.push("meta");
  if (normalized.ctrl) parts.push("ctrl");
  if (normalized.alt) parts.push("alt");
  if (normalized.shift) parts.push("shift");
  parts.push(normalized.key);
  return parts.join("+");
};

/**
 * Human-readable label for a binding.
 *
 * @example
 * formatBindingLabel({ key: "Escape" });
 * // "Esc"
 *
 * @example
 * formatBindingLabel({ key: "n" });
 * // "N"
 */
export const formatBindingLabel = (binding) => {
  const normalized = normalizeBinding(binding);
  if (!normalized) return "—";
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  const parts = [];
  if (normalized.meta) parts.push(isMac ? "⌘" : "Ctrl");
  if (normalized.ctrl) parts.push(isMac ? "⌃" : "Ctrl");
  if (normalized.alt) parts.push(isMac ? "⌥" : "Alt");
  if (normalized.shift) parts.push(isMac ? "⇧" : "Shift");
  const keyLabel =
    normalized.key === "Escape"
      ? "Esc"
      : normalized.key === "Enter"
        ? "Enter"
        : normalized.key === " "
          ? "Space"
          : normalized.key.length === 1
            ? normalized.key.toUpperCase()
            : normalized.key;
  parts.push(keyLabel);
  return parts.join(isMac ? "" : "+");
};

/**
 * Whether focus is in a text field where letter shortcuts should not fire.
 *
 * @example
 * isTypingTarget(document.querySelector("textarea"));
 * // true
 */
export const isTypingTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const element =
    typeof target.closest === "function"
      ? target
      : target.parentElement || null;
  if (!element || typeof element.closest !== "function") return false;
  const tag = String(element.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (element.isContentEditable) return true;
  return Boolean(element.closest('[contenteditable="true"]'));
};

/**
 * Build a binding from a keyboard event.
 *
 * @example
 * bindingFromKeyboardEvent({ key: "j", metaKey: false, ctrlKey: false, altKey: false, shiftKey: false });
 * // { key: "j" }
 */
export const bindingFromKeyboardEvent = (event) => {
  if (!event || event.key == null) return null;
  if (event.key === "Shift" || event.key === "Control" || event.key === "Alt" || event.key === "Meta") {
    return null;
  }
  return normalizeBinding({
    key: event.key,
    meta: Boolean(event.metaKey),
    ctrl: Boolean(event.ctrlKey),
    alt: Boolean(event.altKey),
    shift: Boolean(event.shiftKey) && event.key.length > 1,
  });
};

/**
 * True when the event matches the binding.
 *
 * @example
 * eventMatchesBinding({ key: "j", metaKey: false, ctrlKey: false, altKey: false, shiftKey: false }, { key: "j" });
 * // true
 */
export const eventMatchesBinding = (event, binding) => {
  const expected = normalizeBinding(binding);
  const actual = bindingFromKeyboardEvent(event);
  if (!expected || !actual) return false;
  return bindingFingerprint(expected) === bindingFingerprint(actual);
};

/**
 * Read raw overrides map from localStorage.
 *
 * @example
 * readKeyboardShortcutOverrides("u1");
 * // { "search.focus": { key: "/" } }
 */
export const readKeyboardShortcutOverrides = (userKey = "") => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(keyboardShortcutsStorageKey(userKey));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    /** @type {Record<string, KeyBinding>} */
    const overrides = {};
    for (const [id, value] of Object.entries(parsed)) {
      const normalized = normalizeBinding(value);
      if (normalized) overrides[id] = normalized;
    }
    return overrides;
  } catch (_) {
    return {};
  }
};

/**
 * Persist overrides (only remapped / cleared keys). Empty object clears storage.
 *
 * @example
 * persistKeyboardShortcutOverrides("u1", { "task.add": { key: "c" } });
 */
export const persistKeyboardShortcutOverrides = (userKey, overrides = {}) => {
  if (typeof window === "undefined") return;
  const key = keyboardShortcutsStorageKey(userKey);
  const clean = {};
  for (const [id, value] of Object.entries(overrides || {})) {
    const definition = KEYBOARD_SHORTCUT_DEFINITIONS.find((entry) => entry.id === id);
    if (!definition || !definition.editable) continue;
    const normalized = normalizeBinding(value);
    if (!normalized) continue;
    if (bindingFingerprint(normalized) === bindingFingerprint(definition.defaultBinding)) {
      continue;
    }
    clean[id] = normalized;
  }
  if (Object.keys(clean).length === 0) {
    window.localStorage.removeItem(key);
  } else {
    window.localStorage.setItem(key, JSON.stringify(clean));
  }
  window.dispatchEvent(
    new CustomEvent(KEYBOARD_SHORTCUTS_CHANGED_EVENT, { detail: { userKey } })
  );
};

/**
 * Resolve effective bindings (defaults + overrides). Non-editable always use defaults.
 *
 * @example
 * resolveKeyboardShortcutBindings("u1");
 * // { "search.focus": { key: "/" }, ... }
 */
export const resolveKeyboardShortcutBindings = (userKey = "") => {
  const overrides = readKeyboardShortcutOverrides(userKey);
  /** @type {Record<string, KeyBinding>} */
  const resolved = {};
  for (const definition of KEYBOARD_SHORTCUT_DEFINITIONS) {
    if (!definition.editable) {
      resolved[definition.id] = { ...definition.defaultBinding };
      continue;
    }
    const override = overrides[definition.id];
    resolved[definition.id] = override
      ? { ...override }
      : { ...definition.defaultBinding };
  }
  return resolved;
};

/**
 * Find another shortcut in the same scope that already uses this binding.
 *
 * @example
 * findShortcutBindingConflict("task.next", { key: "k" }, resolveKeyboardShortcutBindings("u1"));
 * // { id: "task.previous", label: "Previous task" }
 */
export const findShortcutBindingConflict = (shortcutId, binding, effectiveBindings) => {
  const definition = KEYBOARD_SHORTCUT_DEFINITIONS.find((entry) => entry.id === shortcutId);
  if (!definition) return null;
  const finger = bindingFingerprint(binding);
  if (!finger) return null;
  for (const other of KEYBOARD_SHORTCUT_DEFINITIONS) {
    if (other.id === shortcutId) continue;
    if (other.scope !== definition.scope) continue;
    const otherBinding = effectiveBindings?.[other.id] || other.defaultBinding;
    if (bindingFingerprint(otherBinding) === finger) {
      return { id: other.id, label: other.label };
    }
  }
  return null;
};

/**
 * Keys that remapping must not assign to editable shortcuts.
 *
 * @example
 * isReservedRemapBinding({ key: "Escape" });
 * // false — Escape is allowed for sidebar.close, blocked only for settings.close via editable:false
 */
export const isForbiddenRemapBinding = (binding) => {
  const normalized = normalizeBinding(binding);
  if (!normalized) return true;
  // Modifier-only already rejected; block bare Tab / function keys for predictability.
  if (/^F\d+$/i.test(normalized.key)) return true;
  if (normalized.key === "Tab") return true;
  return false;
};
